import DatabaseAssetsMethods from './database.assets.methods';
import { InspectDatabase } from './database.structure';
import { AssetModel, InspectionModel, PartLocationModel } from './types';
import { InspectionViewModel, MineLocationViewModel } from './viewmodels.types';

export default class DatabaseInspectionsMethods {
  private db: InspectDatabase;

  constructor(db: InspectDatabase) {
    this.db = db;
  }

  public async getInspections(): Promise<InspectionModel[]> {
    return await this.db.inspections.toArray();
  }

  public async getInspectionHistory({
    assetId,
    mineLocationId,
    equipmentExternalId,
    cutoffDate,
  }: {
    assetId?: number;
    mineLocationId?: number;
    equipmentExternalId?: string;
    cutoffDate: Date;
  }): Promise<InspectionModel[]> {
    const queries: Promise<InspectionModel[]>[] = [];

    if (assetId) {
      queries.push(
        this.db.inspections.where('assetId').equals(assetId).toArray(),
      );
    }

    if (mineLocationId) {
      queries.push(
        this.db.inspections
          .where('mineLocationId')
          .equals(mineLocationId)
          .toArray(),
      );
    }

    if (equipmentExternalId) {
      queries.push(
        this.db.inspections
          .where('equipmentExternalId')
          .equals(equipmentExternalId)
          .toArray(),
      );
    }

    const inspectionResults = await Promise.all(queries);
    const inspections = inspectionResults.flat();

    // Remove the duplicate Inspections by ExternalId, and short by InspectionDate DESC
    const uniqueInspectionsMap = new Map<string, InspectionModel>();
    inspections.forEach((inspection) => {
      uniqueInspectionsMap.set(inspection.externalId, inspection);
    });

    return Array.from(uniqueInspectionsMap.values())
      .sort((a, b) => b.inspectionDate.getTime() - a.inspectionDate.getTime())
      .filter((inspection) => inspection.inspectionDate < cutoffDate);
  }

  public async getPartLocationsByObservationIds(
    observationExternalIds: string[],
  ): Promise<PartLocationModel[]> {
    // First get all inspections that contain these observations
    const inspections = await this.db.inspections
      .filter((inspection) => {
        return inspection.inspectionCategoryTypes.some((categoryType) =>
          categoryType.observations.some((observation) =>
            observationExternalIds.includes(observation.externalId),
          ),
        );
      })
      .toArray();

    // Extract all observableItemIds from the matching observations
    const observableItemIds = new Set<number>();
    inspections.forEach((inspection) => {
      inspection.inspectionCategoryTypes.forEach((categoryType) => {
        categoryType.observations.forEach((observation) => {
          if (observationExternalIds.includes(observation.externalId)) {
            observableItemIds.add(observation.observableItemId);
          }
        });
      });
    });

    // Get part dimensions that match these observable items
    const partDimensions = await this.db.partDimensions
      .where('observableItemId')
      .anyOf([...observableItemIds])
      .toArray();

    // Get equipment parts that contain these parts
    const partIds = [...new Set(partDimensions.map((pd) => pd.partId))];
    const equipmentParts = await this.db.equipmentParts
      .where('partId')
      .anyOf(partIds)
      .toArray();

    // Finally, get the unique part locations
    const locationIds = [...new Set(equipmentParts.map((ep) => ep.locationId))];
    const partLocations = await this.db.partLocations
      .where('id')
      .anyOf(locationIds)
      .toArray();

    return partLocations;
  }

  /**
   * Scoped + batched replacement for {@link getPartLocationsByObservationIds}: one
   * derivation pass for MANY observation ids of a KNOWN inspection, returning the
   * locations per observation id. Same derivation chain (observation → observableItem
   * → partDimensions → partIds → fleet equipment parts fitted with those parts →
   * their locations), but WITHOUT the two measured hot spots of the legacy method:
   * a cursor-walk over every inspection document to find one the caller already
   * knows, and a fleet-wide anyOf fan-out that stepped ~23k equipmentParts rows
   * one cursor call at a time (per-partId equals() batches instead).
   */
  public async getPartLocationsMapByObservationIds(
    inspectionExternalId: string,
    observationExternalIds: string[],
  ): Promise<Map<string, PartLocationModel[]>> {
    const result = new Map<string, PartLocationModel[]>();
    for (const id of observationExternalIds) {
      result.set(id, []);
    }

    const inspection = await this.db.inspections
      .where('externalId')
      .equals(inspectionExternalId)
      .first();
    if (!inspection) {
      return result;
    }

    const wanted = new Set(observationExternalIds);
    const matchingObservations = inspection.inspectionCategoryTypes
      .flatMap((categoryType) => categoryType.observations)
      .filter((observation) => wanted.has(observation.externalId));
    if (matchingObservations.length === 0) {
      return result;
    }

    const isValidKey = (v: unknown): v is number => typeof v === 'number';
    const observableItemIds = [
      ...new Set(matchingObservations.map((o) => o.observableItemId)),
    ].filter(isValidKey);
    const partDimensions = observableItemIds.length
      ? await this.db.partDimensions
          .where('observableItemId')
          .anyOf(observableItemIds)
          .toArray()
      : [];
    const dimensionsByObservableItem = new Map<number, typeof partDimensions>();
    for (const dimension of partDimensions) {
      const list = dimensionsByObservableItem.get(dimension.observableItemId);
      if (list) list.push(dimension);
      else
        dimensionsByObservableItem.set(dimension.observableItemId, [dimension]);
    }

    // Fleet equipment parts fitted with the involved parts - per-partId equals()
    // batches (one getAll each) instead of a single anyOf that walks row-by-row.
    const partIds = [...new Set(partDimensions.map((d) => d.partId))].filter(
      isValidKey,
    );
    const locationIdsByPart = new Map<number, Set<number>>();
    await Promise.all(
      partIds.map(async (partId) => {
        const fitted = await this.db.equipmentParts
          .where('partId')
          .equals(partId)
          .toArray();
        locationIdsByPart.set(
          partId,
          new Set(fitted.map((ep) => ep.locationId).filter(isValidKey)),
        );
      }),
    );

    const allLocationIds = [
      ...new Set([...locationIdsByPart.values()].flatMap((s) => [...s])),
    ];
    const partLocations = allLocationIds.length
      ? await this.db.partLocations.where('id').anyOf(allLocationIds).toArray()
      : [];
    const locationById = new Map(partLocations.map((l) => [l.id, l]));

    for (const observation of matchingObservations) {
      const dimensions =
        dimensionsByObservableItem.get(observation.observableItemId) ?? [];
      const locationIds = new Set(
        dimensions.flatMap((d) => [...(locationIdsByPart.get(d.partId) ?? [])]),
      );
      result.set(
        observation.externalId,
        [...locationIds]
          .sort((a, b) => a - b) // anyOf yields ascending key order - keep it
          .map((id) => locationById.get(id))
          .filter((l): l is PartLocationModel => !!l),
      );
    }

    return result;
  }

  // Helper function to get a single observation from an inspection using externalId
  public findObservationInInspection(
    inspection: InspectionModel,
    observationExternalId: string,
  ) {
    for (const categoryType of inspection.inspectionCategoryTypes) {
      const observation = categoryType.observations.find(
        (obs) => obs.externalId === observationExternalId,
      );
      if (observation) return observation;
    }
    return null;
  }

  public async getPartsByEquipmentId({ id }: { id?: string }) {
    const equipmentParts = await this.db.equipmentParts
      .where('externalEquipmentId')
      .equals(id ?? '')
      .toArray();
    return await this.db.parts
      .where('id')
      .anyOf(equipmentParts.map((ep) => ep.partId))
      .toArray();
  }

  public async getEquipmentsPart({
    partExternalId,
  }: {
    partExternalId: string;
  }) {
    return await this.db.equipmentParts.get(partExternalId);
  }

  public async getEquipmentsByExtenalId(
    externalId: string,
  ): Promise<InspectionViewModel> {
    const inspection = await this.db.inspections
      .where('externalId')
      .equals(externalId)
      .first();

    if (!inspection) {
      throw new Error(`Inspection with externalId ${externalId} not found`);
    }

    const asset = await new DatabaseAssetsMethods(this.db).getAssetById({
      assetId: inspection.assetId,
      effectiveDate: inspection.inspectionDate,
    });

    if (!asset) {
      throw new Error(`Asset with id ${inspection.assetId} not found`);
    }

    const equipments =
      (await this.db.equipments
        .where('assetId')
        .equals(inspection.assetId)
        .toArray()) ?? [];

    asset.equipments = [...equipments];

    // ONE batched derivation for every observation - the previous per-observation
    // getPartLocationsByObservationIds([id]) calls each cursor-walked the whole
    // inspections store and fanned out across the fleet's equipment parts.
    const partLocationsByObservation =
      await this.getPartLocationsMapByObservationIds(
        externalId,
        inspection.inspectionCategoryTypes.flatMap((categoryType) =>
          categoryType.observations.map(
            (observation) => observation.externalId,
          ),
        ),
      );

    const inspectionCategoryTypes = inspection.inspectionCategoryTypes.map(
      (categoryType) => ({
        ...categoryType,
        observations: categoryType.observations.map((observation) => ({
          ...observation,
          partLocations:
            partLocationsByObservation.get(observation.externalId) ?? [],
        })),
      }),
    );

    return {
      inspection: { ...inspection, inspectionCategoryTypes },
      asset,
    };
  }

  public async getInspectionByExternalId(
    externalId: string,
  ): Promise<InspectionViewModel> {
    const inspection = await this.db.inspections
      .where('externalId')
      .equals(externalId)
      .first();

    if (!inspection) {
      throw new Error(`Inspection with externalId ${externalId} not found`);
    }

    let asset: AssetModel | undefined = undefined;
    let mineLocationViewModel: MineLocationViewModel | undefined = undefined;

    if (inspection.assetId > 0) {
      asset = await new DatabaseAssetsMethods(this.db).getAssetById({
        assetId: inspection.assetId,
        effectiveDate: inspection.inspectionDate,
      });

      if (!asset) {
        throw new Error(`Asset with id ${inspection.assetId} not found`);
      }
    } else if (inspection.mineLocationId > 0) {
      const mineLocation = await this.db.mineLocations
        .where('id')
        .equals(inspection.mineLocationId)
        .first();

      if (!mineLocation) {
        throw new Error(
          `MineLocation with id ${inspection.mineLocationId} not found`,
        );
      }

      const site = await this.db.sites
        .where('id')
        .equals(mineLocation.siteId)
        .first();

      const customer = await this.db.customers
        .where('id')
        .equals(site?.customerId ?? 0)
        .first();

      const equipments =
        (await this.db.equipments
          .where('mineLocationId')
          .equals(mineLocation.id)
          .toArray()) ?? [];
      mineLocationViewModel = {
        mineLocation,
        customerId: customer?.id ?? 0,
        customerName: customer?.name ?? '',
        siteId: site?.id ?? 0,
        siteName: site?.name ?? '',
        equipments,
      };
    } else {
      throw new Error(
        `No asset or mineLocation found for inspection with externalId ${externalId}`,
      );
    }

    const inspectionCategoryTypes = inspection.inspectionCategoryTypes.map(
      (categoryType) => {
        return {
          ...categoryType,
          observations: categoryType.observations.map((observation) => {
            return {
              ...observation,
            };
          }),
        };
      },
    );

    return {
      inspection: { ...inspection, inspectionCategoryTypes },
      asset,
      mineLocation: mineLocationViewModel,
    };
  }
}
